import { useGrid } from "./useGrid";
import { useState, useCallback } from "react";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import type { Coordinates } from "@skillscratch/shared-types";

interface GetBlownUpSquaresArgs {
  numberOfSquares: number;
  gamePackId: string;
  gameSessionId: string;
}

interface UseBonusBombsProps {
  scratchesLeft: number;
  treasureFoundCount: number;
  loseStreak: number;
  isStreakLoading: boolean;
}

export function useBonusBombs({
  scratchesLeft,
  treasureFoundCount,
  loseStreak,
  isStreakLoading,
}: UseBonusBombsProps) {
  const { setGrid, gamePackId, gameSessionId, treasureCount } = useGrid();

  const [bonusBombs, setBonusBombs] = useState<number | null>(null);
  const [prefetchedBombs, setPrefetchedBombs] = useState<Coordinates[] | null>(null);
  const [showBonusBombsModal, setShowBonusBombsModal] = useState(false);
  const [isBlowingUpSquares, setIsBlowingUpSquares] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);

  const prefetchBonusBombs = useCallback(async (numberOfSpecialEffectSquares: number) => {
    if (isPrefetching || prefetchedBombs !== null) return;
    
    setIsPrefetching(true);
    
    try {
      const squaresToBlowUp = await callFirebaseFunction<GetBlownUpSquaresArgs, Coordinates[]>(
        'getBlownUpSquares',
        {
          numberOfSquares: numberOfSpecialEffectSquares,
          gamePackId,
          gameSessionId
        }
      );
      
      setPrefetchedBombs(squaresToBlowUp && squaresToBlowUp.length > 0 ? squaresToBlowUp : []);
    } catch (error) {
      console.error("Failed to prefetch bonus bombs:", error);
      setPrefetchedBombs([]);
    } finally {
      setIsPrefetching(false);
    }
  }, [gamePackId, gameSessionId, isPrefetching, prefetchedBombs]);

  const activateBonusBomb = useCallback(async (numberOfSpecialEffectSquares: number) => {
    try {
      let squaresToBlowUp: Coordinates[] = [];
      
      if (prefetchedBombs && prefetchedBombs.length > 0) {
        squaresToBlowUp = prefetchedBombs;
        setPrefetchedBombs(null);
      } else {
        squaresToBlowUp = await callFirebaseFunction<GetBlownUpSquaresArgs, Coordinates[]>(
          'getBlownUpSquares',
          {
            numberOfSquares: numberOfSpecialEffectSquares,
            gamePackId,
            gameSessionId
          }
        ) || [];
      }
      
      if (squaresToBlowUp.length === 0) {
        return Date.now();
      }

      setShowBonusBombsModal(false);
      setIsBlowingUpSquares(true);
      
      setGrid(prevGrid => {
        const newGrid = [...prevGrid.map(row => [...row])];
        
        for (const { x, y } of squaresToBlowUp) {
          if (x >= 0 && x < newGrid[0].length && y >= 0 && y < newGrid.length) {
            newGrid[y][x] = { type: "blownUp" };
          }
        }
        
        return newGrid;
      });

      setBonusBombs(0);
      
      return Date.now();
    } catch (error) {
      console.error("Failed to activate bonus bombs:", error);
      // should probably show an error/handle with sentry/posthog
      return Date.now();
    } finally {
      setIsBlowingUpSquares(false);
    }
  }, [setGrid, gamePackId, gameSessionId, prefetchedBombs]);

  const checkForBonusBombs = useCallback(() => {
    if (isStreakLoading) {
      return;
    }

    const getBonusBombs = (streak: number, treasureCount: number) => {
      const wontHit10TreasuresCondition = scratchesLeft < 6 && scratchesLeft + treasureFoundCount <= 6;

      if (!wontHit10TreasuresCondition || streak < 3) {
        return 0;
      }

      if (streak > 3) {
        return 16;
      }
      
      if (treasureCount >= 4) {
        return 20;
      }

      return 6;
    };
    
    setBonusBombs(prevBombs => {
      if (bonusBombs === 0) {
        return 0;
      }

      const newBonusBombs = prevBombs === null
        ? getBonusBombs(loseStreak, treasureCount) 
        : null;

      if (newBonusBombs && newBonusBombs > 0) {
        prefetchBonusBombs(newBonusBombs);
        setShowBonusBombsModal(true);
        return newBonusBombs;
      }
      
      return null;
    });
  }, [
    isStreakLoading, 
    scratchesLeft, 
    treasureFoundCount, 
    bonusBombs, 
    loseStreak, 
    treasureCount, 
    prefetchBonusBombs
  ]);

  return {
    bonusBombs,
    showBonusBombsModal,
    activateBonusBomb,
    checkForBonusBombs,
    isBlowingUpSquares,
    isPrefetching
  };
}